import React from "react";
import ReactDOM from "react-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Node as ProseNode } from "prosemirror-model";
import { EditorState, NodeSelection, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import { showFeatureModal } from "~/billing";
import { billingUpgradeIconDef } from "~/billing/utils";
import type { ActionItemIntegration, isActionItemIntegratedArgument } from "~/integrations/actionItemIntegration";
import { selectionToJiraText } from "~/integrations/actionItemIntegration/utils";
import { ReactComponent as JiraLogo } from "~/legacy-assets/images/logos/jira.svg";
import type { getPosHandler } from "~/lib/prose/react-node-view";
import type { TooltipProps } from "~/lib/utils";
import {
	type AssignableDropdownItemType,
	disableOnLinkedCopy,
	type DisableOnLinkedCopyArgs,
} from "~/notes/AssignableDropdown";
import type { IntegrationDetailsType } from "~/stream/StreamSettings/ActionItemIntegrations";
import { Button } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";

import { IntegrationLink } from "./IntegrationLink";
import displayPromptInstallingJira from "./PromptInstallingJira";

import styles from "./css/IntegrationLink.module.css";

export const jiraKey = "JIRA";

const jira: ActionItemIntegration = {
	key: jiraKey,
	logo: <JiraLogo className={styles.jiraMenuItem} />,
	displayName: "Jira",
	externalActionItemName: "issue",
	externalContainerType: "project",
	billingFeatureName: "jira_integration",
	isActionItemIntegrated: isActionItemIntegratedWithJira,
	getDropdownMenuItem: getIntegrationItem,
	useIntegrationDetails: useJiraStreamIntegrationDetails,
};

export default jira;

export interface JiraIntegrationLinkProps {
	editorState: EditorState;
	dispatch: (tr: Transaction) => void;
	selection: NodeSelection;
	initialText: string;
	isAccountAdmin?: boolean;
}

function useJiraStreamIntegrationDetails(_integrationOrAsanaStream: IntegrationDetailsType): {
	containerTitle: string;
	createdBy: string;
	includeAiGenerated: boolean;
} {
	throw new Error("useIntegrationDetails hook is not defined for Jira");
}

function isActionItemIntegratedWithJira({ state, position }: isActionItemIntegratedArgument) {
	return !!NodeSelection.create(state.doc, position).node.attrs.linkedIssue;
}

function getIntegrationItem({
	hasAccessToJiraIntegration,
	actionItemLinkedToIntegration,
	view,
	getPos,
	close,
	jira,
	hasAccessToOriginalNote = true,
	isAccountAdmin,
	disabled,
	tooltip,
	...rest
}: {
	hasAccessToJiraIntegration: boolean;
	setBlockAttrs: (values: ProseNode["attrs"]) => unknown;
	actionItemLinkedToIntegration: boolean;
	view: EditorView;
	getPos: getPosHandler;
	close: () => void;
	jira?: { id: string; clientKey: string; baseUrl: string };
	isAccountAdmin?: boolean;
	disabled: boolean;
	tooltip?: TooltipProps;
} & DisableOnLinkedCopyArgs): NonNullable<AssignableDropdownItemType> {
	return {
		id: "jiraIntegration",
		disabled: !hasAccessToOriginalNote || disabled,
		toolTip: tooltip,
		action: (): boolean => {
			if (!jira) {
				if (!hasAccessToJiraIntegration) {
					showFeatureModal("jira_integration");
					close();
					return true;
				}
				displayPromptInstallingJira();
				return true;
			}
			if (typeof getPos !== "boolean") {
				const selection: NodeSelection = NodeSelection.create(view.state.doc, getPos());

				if (selection.node.attrs.linkedIssue) {
					new RemoveIntegrationLink(view.state, view.dispatch.bind(view), selection, view);
				} else {
					if (!hasAccessToJiraIntegration) {
						showFeatureModal("jira_integration");
						close();
						return true;
					}

					const initialText: string = selectionToJiraText(selection);

					new IntegrationLink({
						editorState: view.state,
						dispatch: view.dispatch.bind(view),
						selection: selection,
						initialText,
						isAccountAdmin,
					});
				}
			}
			close();
			return false;
		},
		left: <JiraLogo className={styles.jiraMenuItem} />,
		right: (() => {
			if (typeof getPos !== "boolean") {
				if (actionItemLinkedToIntegration) {
					return <div contentEditable={false}>Unlink from Jira…</div>;
				}
			}
			return (
				<div contentEditable={false} className={styles.menuItemWithCrown}>
					<span>Link to Jira…</span>
					{!hasAccessToJiraIntegration && <FontAwesomeIcon icon={billingUpgradeIconDef} />}
				</div>
			);
		})(),
		...disableOnLinkedCopy({ ...rest, hasAccessToOriginalNote }),
	};
}

class RemoveIntegrationLink {
	private targetDomNode: HTMLElement;
	private readonly root: HTMLDivElement;
	private mounted: boolean;

	constructor(
		editorState: EditorState,
		dispatch: (tr: Transaction) => void,
		selection: NodeSelection,
		view: EditorView,
	) {
		this.root = document.createElement("div");
		this.targetDomNode = document.body;
		this.targetDomNode.appendChild(this.root);

		ReactDOM.render(this.renderRemoveLinkModal(selection, view), this.root);
		this.mounted = true;
	}

	renderRemoveLinkModal = (selection: NodeSelection, view: EditorView) => {
		const unlink = () => {
			const link = selection.node.attrs.linkedIssue;
			const { linkedIssue: _linkedIssue, ...attrsWithoutLinkedIssue } = selection.node.attrs;

			if (link.provider === "JIRA") {
				let tr = view.state.tr.setNodeMarkup(selection.$from.pos, selection.node.type, attrsWithoutLinkedIssue);
				view.dispatch(tr);
			}

			this.closeModal();
		};

		return (
			<Modal onClose={this.closeModal}>
				<Modal.Header noBottomBorder onClose={this.closeModal}>
					<Modal.Title>Are you sure you want to unlink this item?</Modal.Title>
				</Modal.Header>
				<Modal.Body textOnly className={styles.unlinkModalBody}>
					<span>Note:</span> Unlinked items will still exist in Jira. Removing the link will simply turn it back into a
					Fellow action item.
				</Modal.Body>
				<Modal.Footer>
					<Button size="normal" variant="secondary" onClick={this.closeModal}>
						Cancel
					</Button>
					<Button
						size="normal"
						variant="destructive"
						// eslint-disable-next-line react/jsx-no-bind
						onClick={unlink}
					>
						Unlink
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	closeModal = () => {
		if (!this.mounted) return;
		this.mounted = false;
		ReactDOM.unmountComponentAtNode(this.root);
		this.targetDomNode.removeChild(this.root);
	};
}
